import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Reorder, useDragControls } from "framer-motion";
import { Loader2, Plus, Pencil, Trash2, Upload, GripVertical } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { fetchContent, seedContent, createContent, updateContent, deleteContent, uploadContentImage, contentImageUrl, reorderContent } from "../../lib/api";
import { useContent } from "../../context/ContentContext";
import { jobs as staticJobs } from "../../data/jobs";
import { services as staticServices } from "../../data/services";
import { industries as staticIndustries } from "../../data/industries";
import { WORK_AUTH_OPTIONS } from "../../lib/workAuth";

const STATIC = { jobs: staticJobs, services: staticServices, industries: staticIndustries };

const SCHEMAS = {
  jobs: {
    label: "Jobs",
    titleField: "role",
    subField: "location",
    fields: [
      { key: "slug", label: "Slug (URL id)", type: "text", lock: true },
      { key: "role", label: "Role Title", type: "text" },
      { key: "type", label: "Employment Type", type: "text" },
      { key: "location", label: "Location", type: "text" },
      { key: "skill", label: "Skill Category", type: "text" },
      { key: "work_authorization", label: "Work Authorization / Visa", type: "select", options: WORK_AUTH_OPTIONS },
      { key: "summary", label: "Summary", type: "textarea" },
      { key: "responsibilities", label: "Responsibilities (one per line)", type: "list" },
      { key: "requirements", label: "Requirements (one per line)", type: "list" },
    ],
  },
  services: {
    label: "Services",
    titleField: "name",
    subField: "tagline",
    fields: [
      { key: "slug", label: "Slug (URL id)", type: "text", lock: true },
      { key: "name", label: "Name", type: "text" },
      { key: "icon", label: "Lucide icon name", type: "text" },
      { key: "span", label: "Grid span (advanced, e.g. lg:col-span-6)", type: "text" },
      { key: "image", label: "Image", type: "image" },
      { key: "tagline", label: "Tagline", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "problem", label: "The Challenge", type: "textarea" },
      { key: "capabilities", label: "Capabilities (one per line)", type: "list" },
      { key: "cta", label: "CTA label", type: "text" },
    ],
  },
  industries: {
    label: "Industries",
    titleField: "name",
    subField: "tagline",
    fields: [
      { key: "slug", label: "Slug (URL id)", type: "text", lock: true },
      { key: "name", label: "Name", type: "text" },
      { key: "icon", label: "Lucide icon name", type: "text" },
      { key: "group", label: "Group (filter)", type: "text" },
      { key: "image", label: "Image", type: "image" },
      { key: "tagline", label: "Tagline", type: "text" },
      { key: "overview", label: "Overview paragraphs (one per line)", type: "list" },
      { key: "technology", label: "Technology (one per line)", type: "list" },
      { key: "talent", label: "Talent (one per line)", type: "list" },
      { key: "roles", label: "Common Roles (one per line)", type: "list" },
      { key: "cta", label: "CTA label", type: "text" },
    ],
  },
};

const KINDS = ["jobs", "services", "industries"];
const seeded = new Set();

function toForm(item, schema) {
  const f = {};
  schema.fields.forEach((fld) => {
    const v = item?.[fld.key];
    f[fld.key] = fld.type === "list" ? (Array.isArray(v) ? v.join("\n") : "") : (v ?? "");
  });
  return f;
}

function fromForm(form, schema, original) {
  const out = { ...(original || {}) };
  schema.fields.forEach((fld) => {
    if (fld.type === "list") {
      out[fld.key] = (form[fld.key] || "").split("\n").map((s) => s.trim()).filter(Boolean);
    } else {
      out[fld.key] = (form[fld.key] || "").trim();
    }
  });
  return out;
}

export default function AdminCMS({ adminKey }) {
  const { refresh } = useContent();
  const [kind, setKind] = useState("jobs");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { item, isNew }
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [deleteSlug, setDeleteSlug] = useState(null);
  const [uploadingImg, setUploadingImg] = useState(false);
  const fileRef = useRef(null);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const lastOrderRef = useRef("");

  const schema = SCHEMAS[kind];

  const persistOrder = useCallback(async () => {
    const slugs = itemsRef.current.map((i) => i.slug);
    const signature = slugs.join("|");
    if (signature === lastOrderRef.current) return; // no change — skip redundant write
    lastOrderRef.current = signature;
    try {
      await reorderContent(kind, slugs, adminKey);
      refresh();
    } catch {
      toast.error("Could not save the new order");
    }
  }, [kind, adminKey, refresh]);

  const onImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImg(true);
    try {
      const res = await uploadContentImage(file, adminKey);
      setField("image", contentImageUrl(res.path));
      toast.success("Image uploaded");
    } catch {
      toast.error("Image upload failed");
    } finally {
      setUploadingImg(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const loadKind = useCallback(async (k) => {
    setLoading(true);
    try {
      // Seed from bundled defaults the first time (idempotent on the backend)
      if (!seeded.has(k)) {
        await seedContent(k, STATIC[k], adminKey).catch(() => {});
        seeded.add(k);
      }
      const data = await fetchContent(k);
      setItems(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Could not load content");
    } finally {
      setLoading(false);
    }
  }, [adminKey]);

  useEffect(() => { loadKind(kind); }, [kind, loadKind]);

  const openNew = () => {
    setEditing({ item: null, isNew: true });
    setForm(toForm({}, schema));
  };
  const openEdit = (item) => {
    setEditing({ item, isNew: false });
    setForm(toForm(item, schema));
  };

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    const slug = (form.slug || "").trim();
    if (!slug) { toast.error("Slug is required"); return; }
    const payload = fromForm(form, schema, editing.item);
    setBusy(true);
    try {
      if (editing.isNew) {
        await createContent(kind, payload, adminKey);
        toast.success("Item created");
      } else {
        await updateContent(kind, slug, payload, adminKey);
        toast.success("Item updated");
      }
      setEditing(null);
      await loadKind(kind);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save item");
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    const slug = deleteSlug;
    setDeleteSlug(null);
    try {
      await deleteContent(kind, slug, adminKey);
      toast.success("Item deleted");
      await loadKind(kind);
      refresh();
    } catch {
      toast.error("Could not delete item");
    }
  };

  return (
    <div data-testid="admin-cms">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap gap-2">
          {KINDS.map((k) => (
            <button
              key={k}
              onClick={() => setKind(k)}
              className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                kind === k ? "border-sky-400 bg-sky-400 text-ink-900" : "border-white/10 text-slate-300 hover:text-white"
              }`}
              data-testid={`cms-kind-${k}`}
            >
              {SCHEMAS[k].label}
            </button>
          ))}
        </div>
        <button
          onClick={openNew}
          className="inline-flex items-center gap-2 rounded-full bg-sky-400 px-5 py-2.5 text-sm font-medium text-ink-900 hover:bg-sky-300"
          data-testid="cms-add-new"
        >
          <Plus className="h-4 w-4" /> Add {schema.label.slice(0, -1)}
        </button>
      </div>

      <p className="mt-4 text-xs text-slate-500">Drag the handle to reorder how items appear on the website.</p>
      <div className="mt-2 overflow-hidden rounded-2xl border border-white/10">
        {loading ? (
          <div className="flex items-center gap-2 p-8 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-slate-400">No items yet.</div>
        ) : (
          <Reorder.Group axis="y" values={items} onReorder={setItems} className="divide-y divide-white/5">
            {items.map((item) => (
              <CMSRow
                key={item.slug}
                item={item}
                schema={schema}
                onEdit={() => openEdit(item)}
                onDelete={() => setDeleteSlug(item.slug)}
                onCommit={persistOrder}
              />
            ))}
          </Reorder.Group>
        )}
      </div>

      {/* Editor */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[92vh] overflow-y-auto border-white/10 bg-ink-800 text-white sm:max-w-2xl" data-testid="cms-editor">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold tracking-tight">
              {editing?.isNew ? `New ${schema.label.slice(0, -1)}` : `Edit ${schema.label.slice(0, -1)}`}
            </DialogTitle>
            <DialogDescription className="text-slate-400">Edits publish to the live website immediately.</DialogDescription>
          </DialogHeader>
          <div className="mt-2 space-y-4">
            {schema.fields.map((fld) => (
              <div key={fld.key}>
                <label className="mb-1.5 block text-xs font-medium uppercase tracking-[0.15em] text-slate-400">{fld.label}</label>
                {fld.type === "textarea" || fld.type === "list" ? (
                  <textarea
                    value={form[fld.key] || ""}
                    onChange={(e) => setField(fld.key, e.target.value)}
                    rows={fld.type === "list" ? 5 : 3}
                    className="w-full rounded-lg border border-white/10 bg-ink-900 px-3 py-2 text-sm text-white outline-none focus:border-sky-400/70"
                    data-testid={`cms-field-${fld.key}`}
                  />
                ) : fld.type === "select" ? (
                  <select
                    value={form[fld.key] || ""}
                    onChange={(e) => setField(fld.key, e.target.value)}
                    className="w-full rounded-lg border border-white/10 bg-ink-900 px-3 py-2 text-sm text-white outline-none focus:border-sky-400/70"
                    data-testid={`cms-field-${fld.key}`}
                  >
                    <option value="" className="bg-ink-900">Not specified</option>
                    {(fld.options || []).map((opt) => (
                      <option key={opt} value={opt} className="bg-ink-900">{opt}</option>
                    ))}
                  </select>
                ) : fld.type === "image" ? (
                  <div className="space-y-3">
                    {form[fld.key] ? (
                      <img src={form[fld.key]} alt="preview" className="h-32 w-full rounded-lg border border-white/10 object-cover" data-testid="cms-image-preview" />
                    ) : null}
                    <div className="flex flex-wrap items-center gap-3">
                      <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-sky-400 px-4 py-2 text-sm font-medium text-ink-900 hover:bg-sky-300">
                        <Upload className="h-4 w-4" /> {uploadingImg ? "Uploading..." : "Upload image"}
                        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onImageUpload} disabled={uploadingImg} data-testid="cms-image-upload" />
                      </label>
                    </div>
                    <input
                      value={form[fld.key] || ""}
                      onChange={(e) => setField(fld.key, e.target.value)}
                      placeholder="…or paste an image URL"
                      className="w-full rounded-lg border border-white/10 bg-ink-900 px-3 py-2 text-sm text-white outline-none focus:border-sky-400/70"
                      data-testid={`cms-field-${fld.key}`}
                    />
                  </div>
                ) : (
                  <input
                    value={form[fld.key] || ""}
                    onChange={(e) => setField(fld.key, e.target.value)}
                    disabled={fld.lock && !editing?.isNew}
                    className="w-full rounded-lg border border-white/10 bg-ink-900 px-3 py-2 text-sm text-white outline-none focus:border-sky-400/70 disabled:opacity-50"
                    data-testid={`cms-field-${fld.key}`}
                  />
                )}
              </div>
            ))}
            <div className="flex gap-3 pt-2">
              <button onClick={save} disabled={busy} className="rounded-full bg-sky-400 px-6 py-2.5 text-sm font-medium text-ink-900 hover:bg-sky-300 disabled:opacity-60" data-testid="cms-save">
                {busy ? "Saving..." : "Save"}
              </button>
              <button onClick={() => setEditing(null)} className="rounded-full border border-white/15 px-6 py-2.5 text-sm text-white hover:border-white/30">Cancel</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteSlug} onOpenChange={(o) => !o && setDeleteSlug(null)}>
        <AlertDialogContent className="border-white/10 bg-ink-800 text-white" data-testid="cms-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete this item?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This removes it from the live website. This action can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/15 bg-transparent text-white hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 text-white hover:bg-red-400" data-testid="cms-delete-confirm">
              Yes, delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function CMSRow({ item, schema, onEdit, onDelete, onCommit }) {
  const controls = useDragControls();
  return (
    <Reorder.Item
      value={item}
      dragListener={false}
      dragControls={controls}
      onDragEnd={onCommit}
      className="flex items-center justify-between gap-4 bg-ink-800 px-5 py-4"
      data-testid={`cms-row-${item.slug}`}
    >
      <div className="flex min-w-0 items-center gap-3">
        <button
          onPointerDown={(e) => controls.start(e)}
          className="cursor-grab touch-none text-slate-500 hover:text-slate-300 active:cursor-grabbing"
          data-testid={`cms-drag-${item.slug}`}
          aria-label="Drag to reorder"
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <div className="min-w-0">
          <div className="truncate font-medium text-white">{item[schema.titleField] || item.slug}</div>
          <div className="truncate text-sm text-slate-500">{item[schema.subField]}</div>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <button onClick={onEdit} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs text-white hover:border-sky-400/60 hover:text-sky-400" data-testid={`cms-edit-${item.slug}`}>
          <Pencil className="h-3 w-3" /> Edit
        </button>
        <button onClick={onDelete} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs text-slate-400 hover:border-red-400/50 hover:text-red-300" data-testid={`cms-delete-${item.slug}`}>
          <Trash2 className="h-3 w-3" /> Delete
        </button>
      </div>
    </Reorder.Item>
  );
}
